import type { CustomerUser } from '@/types/customer-users'
import { normalizeBankAccount, normalizeGiro } from '@/utils/bankAccount'
import { bankgiroIssue, isValidBankAccount, isValidNormalizedPlusgiroNumber } from '@/utils/validation'
import type { PayoutMethod } from './types'

export type PayoutDetails = NonNullable<CustomerUser['payout_details']>

/** The five field values every payout editor holds (FPayoutMethodFields' models). */
export interface PayoutFields {
  method: PayoutMethod
  clearingNumber: string
  accountNumber: string
  bankgiro: string
  plusgiro: string
}

/** The instruments already on the record — the reference for every change-aware rule. */
export interface StoredPayout {
  bankgiro?: string | null
  plusgiro?: string | null
  bank_account_number?: { clearing_number?: string | null; account_number?: string | null } | null
}

/** Whether a stored giro and a candidate are the same number, ignoring spelling. */
function sameGiro(stored: string | null | undefined, next: string | null | undefined): boolean {
  return normalizeGiro(stored ?? '') === normalizeGiro(next ?? '')
}

/**
 * Whether a field value IS the stored giro, judged normalized. A record with no giro is never "the
 * stored one": absent and all-zero both normalize to '', and a fresh '0000000' must not pass as untouched.
 */
function isStoredGiro(stored: string | null | undefined, value: string): boolean {
  const normalizedStored = normalizeGiro(stored ?? '')
  return normalizedStored !== '' && normalizeGiro(value) === normalizedStored
}

/**
 * Whether the account fields still hold the stored pair, judged normalized on both sides like
 * `buildPayoutDetailsPatch`.
 */
function isStoredBankAccount(stored: StoredPayout['bank_account_number'], fields: PayoutFields): boolean {
  if (stored?.clearing_number == null || stored.account_number == null) return false
  const storedNormalized = normalizeBankAccount(stored.clearing_number, stored.account_number)
  const candidate = normalizeBankAccount(fields.clearingNumber, fields.accountNumber)
  return (
    storedNormalized.clearingNumber === candidate.clearingNumber &&
    storedNormalized.accountNumber === candidate.accountNumber
  )
}

/**
 * The payout details to PATCH, or null when nothing changed. Shared by the profile dialog and the
 * profile page so the two editors of `payout_details` cannot disagree.
 */
export function buildPayoutDetailsPatch(
  current: CustomerUser,
  fields: PayoutFields,
  options: { isCompany: boolean }
): PayoutDetails | null {
  const hasAccountInput = fields.clearingNumber !== '' || fields.accountNumber !== ''
  const candidate = normalizeBankAccount(fields.clearingNumber, fields.accountNumber)
  const account =
    fields.method === 'bank_account' && hasAccountInput
      ? { clearing_number: candidate.clearingNumber, account_number: candidate.accountNumber }
      : null

  // The selected method wins and the other two instruments are nulled: the refund wizard resolves a
  // record holding several by a fixed priority, bank account first. `iban_bic` is spread through as is.
  const next: PayoutDetails = {
    ...(current.payout_details ?? {}),
    bank_account_number: account,
    bankgiro: fields.method === 'bankgiro' && fields.bankgiro !== '' ? normalizeGiro(fields.bankgiro) : null,
    plusgiro: fields.method === 'plusgiro' && fields.plusgiro !== '' ? normalizeGiro(fields.plusgiro) : null,
  }

  // Bank transfer is hidden for a company, so a stored account is nothing the editor can show or keep:
  // it is left out of the comparison and cannot raise a change by itself (it is still nulled on save).
  const stored = options.isCompany ? null : (current.payout_details?.bank_account_number ?? null)
  // Compared NORMALIZED on both sides: a stored value can predate the normalization rules, and a raw
  // comparison would read as a change that is not one, nulling the other instruments as a side effect.
  const storedNormalized = stored == null ? null : normalizeBankAccount(stored.clearing_number, stored.account_number)

  const unchanged =
    (storedNormalized?.clearingNumber ?? '') === (account?.clearing_number ?? '') &&
    (storedNormalized?.accountNumber ?? '') === (account?.account_number ?? '') &&
    sameGiro(current.payout_details?.bankgiro, next.bankgiro) &&
    sameGiro(current.payout_details?.plusgiro, next.plusgiro)

  return unchanged ? null : next
}

/**
 * Whether the payout fields hold something the api would reject (422), so the save must be held.
 * Empty passes in every gate: clearing the fields is a valid no-op. Form shape is the v-form's job.
 */
export function payoutGateBlocked(fields: PayoutFields, stored: StoredPayout): boolean {
  // The registry gate plus the bank's account cap. Change-aware like the giro gates below: a stored
  // pair left untouched is neither PATCHed nor re-judged server-side.
  if (
    fields.method === 'bank_account' &&
    (fields.clearingNumber !== '' || fields.accountNumber !== '') &&
    !isStoredBankAccount(stored.bank_account_number, fields) &&
    !isValidBankAccount(fields.clearingNumber, fields.accountNumber)
  ) {
    return true
  }
  // The check-digit gate for bankgiro. Change-aware like the api's own rule: a stored number left
  // untouched is skipped server-side, so it must not hold unrelated edits hostage here either.
  if (
    fields.method === 'bankgiro' &&
    fields.bankgiro !== '' &&
    !isStoredGiro(stored.bankgiro, fields.bankgiro) &&
    bankgiroIssue(fields.bankgiro) !== undefined
  ) {
    return true
  }
  // Only the NORMALIZED length blocks a plusgiro ('00' and '007' strip below the two-digit floor);
  // the check digit merely warns. Change-aware for the same reason as bankgiro.
  if (
    fields.method === 'plusgiro' &&
    fields.plusgiro !== '' &&
    !isStoredGiro(stored.plusgiro, fields.plusgiro) &&
    !isValidNormalizedPlusgiroNumber(fields.plusgiro)
  ) {
    return true
  }
  return false
}
